/**
 * Copy for the two income-lane screens.
 *
 * The wording was supplied by Scott on 2026-09-05 in the Notion task and is used verbatim. Cat's
 * team may still revise it against the AnswerConnect call transcripts; substituting it is an edit
 * to this file plus a rebuild, which is exactly why the strings live here and not in the JSX.
 *
 * `copyId` is versioned WITH the text: it is what analytics correlates conversion against, and it
 * is the identifier Strapi mirrors, so a divergence between the two surfaces is detectable at all.
 * Change the text and the id together, never one without the other.
 *
 * OD-15 — what may NOT appear in `borderline.body`: no amount and no currency symbol. That entry is
 * the sentence shared word for word with the Strapi landing pages, and Allybot localises its price
 * per country while Strapi does not. The price line is therefore a separate, Allybot-local string
 * that stays in 10_LowIncomeDisclaimerAgreement.jsx together with its currency machinery. Two
 * pieces on one screen, two places in the code. Do not merge them in either direction.
 *
 * None of these strings is ever persisted or used as an enum value. Only the 'yes' / 'no' tokens
 * reach the database, which is why the option labels below can change freely while
 * `optionValues` cannot — see the note on that key.
 *
 * @author Cristian David
 * @since 2026-09-11
 */

export const laneCopy = {
  borderline: {
    copyId: 'borderline-disclosure-2026-09-05',

    // The shared sentence. Carries no figure and no currency symbol (OD-15). This is the only
    // string on this screen that Strapi renders too, and the only one copy parity compares.
    body: [
      'Your matchmaker does the searching: our members, our partner matchmakers, and your dating ' +
        'apps, which she runs for you, messaging included. You approve every match before you meet ' +
        'anyone.',
    ],

    question: "If you knew we could find your ideal match, is this an investment you'd want to make?",

    // Labels only. The VALUES stay 'yes' and 'no' because `lowIncomeDisclaimerAgreement` is a
    // Mongoose enum of ['', 'yes', 'no'] (models/allybot_sessions.js): sending anything else
    // returns a 400 that the client swallows, leaving the Next button dead for every visitor in
    // this lane. Relabelling is safe; revaluing is not.
    optionLabels: {
      yes: "Yes, I'd make that work",
      no: "That's more than I want to spend",
    },
  },

  belowFloor: {
    copyId: 'below-floor-optin-2026-09-05',

    // Two openings for one screen. Which one is shown depends on how the visitor arrived, and the
    // signal is `state.lowIncomeDisclaimerAgreement`: only someone who saw the price screen and
    // declined it can hold 'no' there, because agreeing routes to booking instead. A visitor sorted
    // below the floor at step 8 never reaches that screen, so the field is still empty for them.
    openingFromIncome: "Thanks, {name}. Based on what you've shared, the best place to start is " +
      "VIDA's member network, and it's free.",
    openingFromPrice: "That's useful to know, and I appreciate you being straight with me. The " +
      "right place to start, then, is VIDA's member network, and it's free.",

    body: [
      'Our matchmakers search the network every day for the clients they’re working with. When ' +
        "you're a fit for one of them, a matchmaker reaches out to you directly.",
    ],

    emailPrompt: "What's the best email to reach you at?",
    consentNote: "By joining, your profile details can be shown to VIDA's matchmakers and their clients.",
    submitLabel: 'Add me to the network',

    // Step 16 — shown after the opt-in saves the lead. Informational only: the record is already in,
    // so this screen just confirms it and offers a way back to the site. {name} is filled from state.
    confirmation: {
      body: [
        "You're in, {name}. Your details are now part of VIDA's member network.",
        "From here our matchmakers do the searching. If you're a fit for one of the clients they're " +
          "working with, a matchmaker reaches out to you directly, so there's nothing else you need to do.",
      ],
      buttonLabel: 'Go to VIDA Select',
    },
  },
};

// No price helper lives here (OD-15). The price line and its per-currency conversions stay in
// client/src/Components/Steps/10_LowIncomeDisclaimerAgreement.jsx.
